import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import {
  Session,
  SessionState,
  SessionForm,
  SessionBoard,
  SessionQueue,
  SessionAlert,
  SessionActivityItem,
  WorklistSettings,
} from '@crowdctrl/core';

type SessionActionBody = {
  tenantId: string;
  workspaceInstanceId: string;
  expectedUpdatedAt?: string;
};

type SessionUpdateBody = {
  thumbnailUrl?: string;
  name?: string;
  tenantId: string;
  workspaceInstanceId: string;
};

type SessionCreateBody = {
  workspaceInstanceId: string;
  name: string;
  tenantId: string;
};

type CustomQueuePayload = {
  name: string;
  description?: string;
  capacity?: number;
  settings?: any;
  expectedUpdatedAt?: string;
};

/**
 * Service for workspace session management API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceSessionService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all sessions for a workspace instance with optional pagination and filters.
   */
  getSessions(
    tenantId: string,
    workspaceInstanceId: string,
    status?: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    sessions: Session[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      tenantId: tenantId,
      workspaceInstanceId: workspaceInstanceId,
    };
    if (status) {
      params['status'] = status;
    }
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        sessions: Session[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/workspace_sessions_api/api/workspace-sessions`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single session by ID
   */
  getSession(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
  ): Observable<{ session: Session }> {
    return from(
      this.apiService.get<{ session: Session }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}`,
        { tenantId: tenantId, workspaceInstanceId: workspaceInstanceId },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new session
   */
  createSession(
    tenantId: string,
    sessionData: { workspaceInstanceId: string; name: string },
  ): Observable<{ session: Session }> {
    return from(
      this.apiService.post<{ session: Session }, SessionCreateBody>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions`,
        {
          ...sessionData,
          tenantId: tenantId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update an existing session
   */
  updateSession(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
    sessionData: { name?: string; thumbnailUrl?: string },
  ): Observable<{ session: Session }> {
    return from(
      this.apiService.put<{ session: Session }, SessionUpdateBody>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}`,
        {
          ...sessionData,
          tenantId: tenantId,
          workspaceInstanceId: workspaceInstanceId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update thumbnail URL for a session
   */
  updateThumbnail(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
    thumbnailUrl: string | null,
  ): Observable<{ session: Session }> {
    return this.updateSession(tenantId, workspaceInstanceId, sessionId, {
      thumbnailUrl: thumbnailUrl || undefined,
    } as { name?: string; thumbnailUrl?: string });
  }

  /**
   * Delete a session
   */
  deleteSession(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
  ): Observable<{ success: boolean }> {
    // Note: tenantId should be validated server-side from auth token
    // Pass tenantId and workspaceInstanceId as query parameters in URL
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}?tenantId=${encodeURIComponent(tenantId)}&workspaceInstanceId=${encodeURIComponent(workspaceInstanceId)}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Open a session (Draft → Open) (with optional optimistic locking timestamp)
   */
  openSession(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
    expectedUpdatedAt?: string,
  ): Observable<{ session: Session }> {
    const body: SessionActionBody = {
      tenantId: tenantId,
      workspaceInstanceId: workspaceInstanceId,
    };
    if (expectedUpdatedAt) {
      body.expectedUpdatedAt = expectedUpdatedAt;
    }
    return from(
      this.apiService.put<{ session: Session }, SessionActionBody>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/open`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Pause a session (Open → Paused) (with optional optimistic locking timestamp)
   */
  pauseSession(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
    expectedUpdatedAt?: string,
  ): Observable<{ session: Session }> {
    const body: SessionActionBody = {
      tenantId: tenantId,
      workspaceInstanceId: workspaceInstanceId,
    };
    if (expectedUpdatedAt) {
      body.expectedUpdatedAt = expectedUpdatedAt;
    }
    return from(
      this.apiService.put<{ session: Session }, SessionActionBody>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/pause`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Resume a session (Paused → Open) (with optional optimistic locking timestamp)
   */
  resumeSession(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
    expectedUpdatedAt?: string,
  ): Observable<{ session: Session }> {
    const body: SessionActionBody = {
      tenantId: tenantId,
      workspaceInstanceId: workspaceInstanceId,
    };
    if (expectedUpdatedAt) {
      body.expectedUpdatedAt = expectedUpdatedAt;
    }
    return from(
      this.apiService.put<{ session: Session }, SessionActionBody>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/resume`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Close a session (Open or Paused → Closed) (with optional optimistic locking timestamp)
   */
  closeSession(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
    expectedUpdatedAt?: string,
  ): Observable<{ session: Session }> {
    const body: SessionActionBody = {
      tenantId: tenantId,
      workspaceInstanceId: workspaceInstanceId,
    };
    if (expectedUpdatedAt) {
      body.expectedUpdatedAt = expectedUpdatedAt;
    }
    return from(
      this.apiService.put<{ session: Session }, SessionActionBody>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/close`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a custom queue in a session
   */
  createCustomQueue(
    tenantId: string,
    workspaceId: string,
    sessionId: string,
    queueData: CustomQueuePayload,
  ): Observable<{ session: Session; queue: SessionQueue }> {
    const body: CustomQueuePayload & { tenantId: string; workspaceId: string } = {
      tenantId: tenantId,
      workspaceId: workspaceId,
      ...queueData,
    };
    return from(
      this.apiService.post<
        { session: Session; queue: SessionQueue },
        CustomQueuePayload & { tenantId: string; workspaceId: string }
      >(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/queues?tenantId=${encodeURIComponent(tenantId)}&workspaceId=${encodeURIComponent(workspaceId)}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Delete a custom queue from a session
   */
  deleteCustomQueue(
    tenantId: string,
    workspaceId: string,
    sessionId: string,
    queueId: string,
    expectedUpdatedAt?: string,
  ): Observable<{ session: Session; success: boolean }> {
    let url = `/workspace_sessions_api/api/workspace-sessions/${sessionId}/queues/${queueId}?tenantId=${encodeURIComponent(tenantId)}&workspaceId=${encodeURIComponent(workspaceId)}`;
    if (expectedUpdatedAt) {
      url += `&expectedUpdatedAt=${encodeURIComponent(expectedUpdatedAt)}`;
    }
    return from(
      this.apiService.delete<{ session: Session; success: boolean }>(this.baseUrl, url),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get order counts for a session.
   * staleByStatus and recomputeScheduledAtByStatus support per-row spinners.
   */
  getSessionOrderCounts(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
    scheduleRecompute?: boolean,
    status?: string,
  ): Observable<{
    counts: Record<string, number>;
    orderFlow?: {
      stages: Array<{ key: string; label: string; count: number }>;
      total: number;
      maxCount: number;
    };
    retrievedAt: string;
    cacheUpdatedAt?: string;
    staleByStatus?: Record<string, boolean>;
    recomputeScheduledAtByStatus?: Record<string, string>;
    recomputeFailedAtByStatus?: Record<string, string>;
    recomputeErrorByStatus?: Record<string, string>;
  }> {
    const params: Record<string, string> = {
      tenantId,
      workspaceInstanceId,
    };
    if (scheduleRecompute === true) params['scheduleRecompute'] = 'true';
    if (status) params['status'] = status;
    return from(
      this.apiService.get<{
        counts: Record<string, number>;
        orderFlow?: {
          stages: Array<{ key: string; label: string; count: number }>;
          total: number;
          maxCount: number;
        };
        retrievedAt: string;
        cacheUpdatedAt?: string;
        staleByStatus?: Record<string, boolean>;
        recomputeScheduledAtByStatus?: Record<string, string>;
        recomputeFailedAtByStatus?: Record<string, string>;
        recomputeErrorByStatus?: Record<string, string>;
      }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/order-counts`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get pending order counts per queue for a session
   */
  getSessionQueueOrderCounts(
    tenantId: string,
    workspaceInstanceId: string,
    sessionId: string,
  ): Observable<{ counts: Record<string, number>; retrievedAt: string }> {
    return from(
      this.apiService.get<{
        counts: Record<string, number>;
        retrievedAt: string;
      }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/queue-order-counts`,
        {
          tenantId: tenantId,
          workspaceInstanceId: workspaceInstanceId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List form controls from a session's formControls subcollection with pagination
   */
  listSessionFormControls(
    sessionId: string,
    workspaceInstanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: SessionForm[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {
      workspaceInstanceId: workspaceInstanceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        items: SessionForm[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/formControls`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update a form control in a session's formControls subcollection with optimistic locking
   */
  updateSessionFormControl(
    sessionId: string,
    workspaceInstanceId: string,
    formId: string,
    updates: {
      enabled?: boolean;
      name?: string;
      inputMode?: 'alphabetic' | 'numeric';
      defaultSelectedQueueId?: string | null;
      settings?: SessionForm['settings'];
    },
    version: number,
  ): Observable<{ form: SessionForm }> {
    const body: {
      enabled?: boolean;
      name?: string;
      inputMode?: 'alphabetic' | 'numeric';
      defaultSelectedQueueId?: string | null;
      settings?: SessionForm['settings'];
      version: number;
    } = {
      version: version,
    };
    if (updates.enabled !== undefined) {
      body.enabled = updates.enabled;
    }
    if (updates.name !== undefined) {
      body.name = updates.name;
    }
    if (updates.inputMode !== undefined) {
      body.inputMode = updates.inputMode;
    }
    if (updates.defaultSelectedQueueId !== undefined) {
      body.defaultSelectedQueueId = updates.defaultSelectedQueueId;
    }
    if (updates.settings !== undefined) {
      body.settings = updates.settings;
    }
    return from(
      this.apiService.put<{ form: SessionForm }, typeof body>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/formControls/${formId}?workspaceInstanceId=${encodeURIComponent(workspaceInstanceId)}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List board controls from a session's boardControls subcollection with optional pagination.
   */
  listSessionBoardControls(
    sessionId: string,
    workspaceInstanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: SessionBoard[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {
      workspaceInstanceId: workspaceInstanceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        items: SessionBoard[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/boardControls`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update a board control in a session's boardControls subcollection
   */
  updateSessionBoardControl(
    sessionId: string,
    workspaceInstanceId: string,
    boardId: string,
    updates: {
      enabled?: boolean;
      name?: string;
      hiddenQueueIds?: string[];
      waitingLayoutMode?: 'flat' | 'queues';
      audioEnabled?: boolean;
    },
    version: number,
  ): Observable<{ board: SessionBoard }> {
    const body: {
      enabled?: boolean;
      name?: string;
      hiddenQueueIds?: string[];
      waitingLayoutMode?: 'flat' | 'queues';
      audioEnabled?: boolean;
      version: number;
    } = {
      version: version,
    };
    if (updates.enabled !== undefined) {
      body.enabled = updates.enabled;
    }
    if (updates.name !== undefined) {
      body.name = updates.name;
    }
    if (updates.hiddenQueueIds !== undefined) {
      body.hiddenQueueIds = updates.hiddenQueueIds;
    }
    if (updates.waitingLayoutMode !== undefined) {
      body.waitingLayoutMode = updates.waitingLayoutMode;
    }
    if (updates.audioEnabled !== undefined) {
      body.audioEnabled = updates.audioEnabled;
    }
    return from(
      this.apiService.put<{ board: SessionBoard }, typeof body>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/boardControls/${boardId}?workspaceInstanceId=${encodeURIComponent(workspaceInstanceId)}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List queue controls from a session's queueControls subcollection with optional pagination.
   */
  listSessionQueueControls(
    sessionId: string,
    workspaceInstanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: SessionQueue[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {
      workspaceInstanceId: workspaceInstanceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        items: SessionQueue[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/queueControls`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Single GET for worklist settings (queues today; more fields later).
   * Pass {@link tenantUserId} so the backend can scope per-user prefs when implemented;
   * {@link sessionMemberId} is optional for the same reason.
   */
  getWorklistSettings(
    sessionId: string,
    workspaceInstanceId: string,
    tenantUserId: string,
    sessionMemberId?: string | null,
  ): Observable<WorklistSettings> {
    const params: Record<string, string> = {
      workspaceInstanceId,
      tenantUserId,
    };
    if (sessionMemberId) {
      params['sessionMemberId'] = sessionMemberId;
    }
    return from(
      this.apiService.get<WorklistSettings>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/worklist-settings`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List alerts for a session with optional pagination.
   */
  listSessionAlerts(
    sessionId: string,
    workspaceInstanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: SessionAlert[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {
      workspaceInstanceId: workspaceInstanceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        items: SessionAlert[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/alerts`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List session-level activity (e.g. serve / leave service) with optional pagination.
   */
  listSessionActivities(
    sessionId: string,
    workspaceInstanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: SessionActivityItem[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {
      workspaceInstanceId: workspaceInstanceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        items: SessionActivityItem[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/activities`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create an alert for a session.
   */
  createSessionAlert(
    sessionId: string,
    workspaceInstanceId: string,
    alert: Omit<SessionAlert, 'id' | 'createdAt'>,
  ): Observable<{ alert: SessionAlert }> {
    const body: Record<string, unknown> = {
      workspaceInstanceId,
      name: alert.name,
      scope: alert.scope,
      metric: alert.metric,
      comparator: alert.comparator,
      value: alert.value,
    };

    if (alert.queueIds?.length) {
      body['queueIds'] = alert.queueIds;
    } else if (alert.queueId) {
      body['queueId'] = alert.queueId;
    }
    if (alert.description) {
      body['description'] = alert.description;
    }

    return from(
      this.apiService.post<{ alert: SessionAlert }, typeof body>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/alerts`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single session alert by id.
   */
  getSessionAlert(
    sessionId: string,
    workspaceInstanceId: string,
    alertId: string,
  ): Observable<{ alert: SessionAlert }> {
    return from(
      this.apiService.get<{ alert: SessionAlert }>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/alerts/${alertId}`,
        { workspaceInstanceId },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update a session alert by id.
   */
  updateSessionAlert(
    sessionId: string,
    workspaceInstanceId: string,
    alertId: string,
    updates: Partial<Omit<SessionAlert, 'id' | 'createdAt'>>,
  ): Observable<{ alert: SessionAlert }> {
    const body = { ...updates, workspaceInstanceId };
    return from(
      this.apiService.put<{ alert: SessionAlert }, typeof body>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/alerts/${alertId}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Delete a session alert by id.
   */
  deleteSessionAlert(
    sessionId: string,
    workspaceInstanceId: string,
    alertId: string,
  ): Observable<{ success: boolean }> {
    const url = `/workspace_sessions_api/api/workspace-sessions/${sessionId}/alerts/${alertId}`;
    return from(
      this.apiService.delete<{ success: boolean }>(this.baseUrl, url, {
        workspaceInstanceId,
      }),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update a queue control in a session's queueControls subcollection with optimistic locking
   */
  updateSessionQueueControl(
    sessionId: string,
    workspaceInstanceId: string,
    queueId: string,
    updates: { enabled?: boolean; name?: string },
    version: number,
  ): Observable<{ queue: SessionQueue }> {
    const body: { enabled?: boolean; name?: string; version: number } = {
      version: version,
    };
    if (updates.enabled !== undefined) {
      body.enabled = updates.enabled;
    }
    if (updates.name !== undefined) {
      body.name = updates.name;
    }
    return from(
      this.apiService.put<{ queue: SessionQueue }, typeof body>(
        this.baseUrl,
        `/workspace_sessions_api/api/workspace-sessions/${sessionId}/queueControls/${queueId}?workspaceInstanceId=${encodeURIComponent(workspaceInstanceId)}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * @deprecated Use listSessionFormControls instead
   */
  listSessionForms(
    sessionId: string,
    workspaceInstanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: SessionForm[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    return this.listSessionFormControls(sessionId, workspaceInstanceId, limit, cursor);
  }

  /**
   * @deprecated Use updateSessionFormControl instead
   */
  updateSessionForm(
    sessionId: string,
    workspaceInstanceId: string,
    formId: string,
    updates: {
      enabled?: boolean;
      name?: string;
      inputMode?: 'alphabetic' | 'numeric';
    },
    version: number,
  ): Observable<{ form: SessionForm }> {
    return this.updateSessionFormControl(sessionId, workspaceInstanceId, formId, updates, version);
  }

  /**
   * @deprecated Use listSessionBoardControls instead
   */
  listSessionBoards(
    sessionId: string,
    workspaceInstanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: SessionBoard[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    return this.listSessionBoardControls(sessionId, workspaceInstanceId, limit, cursor);
  }
}
